import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Path, Tree } from '@s8l/client-tree-lib';

import { StatUserLabel } from 'src/app/services/jsonrpc/cms-server.service';
import { CmsService } from 'src/app/services/cms.service';
import { Journey, JourneyElement, JourneyService } from 'src/app/services/journey.service';
import { NotificationsService } from 'src/app/services/notifications.service';

@Component({
  selector: 'app-journey-edit',
  templateUrl: './journey-edit.component.html',
  styleUrls: ['./journey-edit.component.scss']
})
export class JourneyEditComponent implements OnInit {
  private _loading = false;

  public tree: Tree;
  public journey: Journey = null;
  public labels: StatUserLabel[];
  public labelOptions: any;
  public changed = false;

  private names: string[]; // prevent duplicate names

  public get loading(): boolean {
    return this._loading || !this.tree || !this.labels;
  }

  public set loading(flag: boolean) {
    this._loading = flag;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cms: CmsService,
    private journeys: JourneyService,
    private notification: NotificationsService
  ) {}

  public ngOnInit() {
    this.cms.cms.statsLabelsList().then(labels => {
      this.labelOptions = {};
      labels.forEach(l => (this.labelOptions[l.key] = l.values));
      this.labels = labels;
    });
    this.cms.tree.asObservable().subscribe(tree => (this.tree = tree));
    this.route.paramMap.subscribe(async map => this.init(map.get('uuid')));
  }

  public get maxDepth(): number {
    return this.cms.config.maxDepth();
  }

  public get error(): string {
    if (this.journey.name.trim() == '') return 'Please provide a name for the journey.';
    if (this.names.includes(this.journey.name)) return 'The name of the journey already exists. Please provide a unique name.';
    if (!this.journey.children?.length) return 'Please add at least one path for the journey.';
    const paths = this.journey.children.map(c => c.node?.path);
    if (paths.some(p => !p)) return 'At least one path does not exist. Please select existing paths.';
    if (paths.some(p => Path.parse(p).length <= 1)) return 'Empty / root paths are not allowed. Please remove empty paths.';
    if (paths.some((p, i) => paths.indexOf(p) !== i)) return 'Duplicate paths are not allowed. Please remove all duplicates.';
    if (this.journey.filters.some(f => f.key === '' || f.value === ''))
      return 'Please ensure every filter has a valid key and value selected.';
    return null;
  }

  private async init(uuid: string) {
    this.loading = true;
    const all = await this.journeys.list();
    if (uuid === 'new') {
      this.names = all.map(j => j.name);
      this.journey = { name: 'New Journey', filters: [], children: [] };
      this.loading = false;
    } else {
      this.names = all.filter(j => j.uuid !== uuid).map(j => j.name);
      this.journey = all.find(j => j.uuid === uuid);
      this.loading = false;
      if (this.journey == null) {
        return this.cancel();
      }
    }
  }

  public onElementChange(index: number, element: JourneyElement) {
    if (element.node?.path) {
      this.changed = true;
      const path = Path.parse(element.node.path);
      if (path.length <= 1) {
        this.journey.children.splice(index, 1);
      }
    }
  }

  public addPath() {
    this.changed = true;
    this.journey.children.push({
      node: this.cms.tree.value?.root,
      children: []
    });
  }

  public addFilter() {
    this.changed = true;
    this.journey.filters.push({ key: '', value: '', positive: true });
  }

  public async submit() {
    this.loading = true;
    const create = !this.journey.uuid;
    try {
      const updated = await this.journeys.upsert(this.journey);
      await this.notification.notify(
        'success',
        create ? 'Created' : 'Updated',
        `The Journey was successfully ${create ? 'created' : 'updated'}!`
      );
      await this.router.navigate(['/journey', updated.uuid]);
    } finally {
      this.changed = false;
      this.loading = false;
    }
  }

  public cancel() {
    return this.router.navigate(['/journey']);
  }
}
